import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense, startTransition } from "react";
import Cookies from "js-cookie";
import { supabase } from "./utils/supabaseClient";
import { withTimeout } from "./utils/withTimeout";
import { readFreshCache, writeCache } from "./utils/clientCache";
import { handleExportDataLocal, handleImportDataLocal } from './utils/dataHandlers';
import { callGeminiVisionUltimate } from './services/aiService';
import { TRANSLATIONS } from './constants';
import { THEMES, DARK_TOGGLE_MAP } from './constants/themes';

const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const SystemStatus = lazy(() => import('./pages/SystemStatus'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginScreen = lazy(() => import('./pages/auth/LoginScreen'));
const SummaryTab = lazy(() => import('./pages/dashboard/SummaryTab'));
const HoldingTab = lazy(() => import('./pages/dashboard/HoldingTab'));
const TransactionTab = lazy(() => import('./pages/dashboard/TransactionTab'));
const DividendTab = lazy(() => import('./pages/dashboard/DividendTab'));
const CapitalTab = lazy(() => import('./pages/dashboard/CapitalTab'));
const AdminTab = lazy(() => import('./pages/admin/AdminTab'));
const FundamentalTab = lazy(() => import('./pages/dashboard/FundamentalTab'));
const XDTab = lazy(() => import('./pages/dashboard/XDTab'));
const InsightsTab = lazy(() => import('./pages/dashboard/InsightsTab'));
const RealizedPLTab = lazy(() => import('./pages/dashboard/RealizedPLTab'));
const FinancialReport = lazy(() => import('./pages/dashboard/FinancialReport'));
const PivotTab = lazy(() => import('./pages/dashboard/PivotTab'));
const TaxTab = lazy(() => import('./pages/dashboard/TaxTab'));
const TradingViewTicker = lazy(() => import('./components/common/TradingViewTicker'));
const NotificationBell = lazy(() => import('./components/common/NotificationBell'));
const ProfileDropdown = lazy(() => import('./components/common/ProfileDropdown'));
const CookieBanner = lazy(() => import('./components/common/CookieBanner'));
const IncidentButton = lazy(() => import('./components/common/IncidentButton'));
const UpgradeModal = lazy(() => import('./components/common/UpgradeModal'));

import TransactionForm from './components/forms/TransactionForm';
import DividendForm from './components/forms/DividendForm';
import CapitalForm from './components/forms/CapitalForm';
import AccessibleDialog from './components/common/AccessibleDialog';
import ConfirmActionDialog from './components/common/ConfirmActionDialog';
import {
  SummarySkeleton,
  HoldingSkeleton,
  TransactionSkeleton,
  DividendSkeleton,
  CapitalSkeleton,
  XDSkeleton,
  InsightsSkeleton,
  RealizedPLSkeleton,
  FundamentalSkeleton,
  AllocationSkeleton,
} from './components/skeletons/SkeletonLoaders';
import { TrendingUp, Search, Loader2, Sun, Moon, Check, Shield, Coffee, ShieldAlert, LayoutDashboard, PieChart, ArrowLeftRight, Landmark, DollarSign, CircleCheckBig, BriefcaseBusiness, CalendarDays, TableProperties, LineChart, ReceiptText, Menu, X, Plus } from "lucide-react";

const MARKET_PROXY_URL = process.env.REACT_APP_PIVOT_PROXY_URL;
const MARKET_SNAPSHOT_CACHE_KEY = "xfolyo_market_snapshot_v1";
const MARKET_SNAPSHOT_CACHE_TTL_MS = 60 * 1000;
const PUBLIC_FX_CACHE_KEY = "xfolyo_public_fx_v1";
const PUBLIC_FX_CACHE_TTL_MS = 15 * 60 * 1000;
const PUBLIC_FX_REFRESH_INTERVAL_MS = PUBLIC_FX_CACHE_TTL_MS;
const DIVIDEND_PATTERNS_CACHE_KEY = "xfolyo_dividend_patterns_v1";
const DIVIDEND_PATTERNS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// ── Suppress noisy dev warnings ──────────────────────────────────────────────
if (typeof window !== "undefined") {
  const originalError = console.error;
  console.error = (...args) => {
    const msg = typeof args[0] === 'string' ? args[0] : '';
    if (
      msg.includes("ResizeObserver loop") ||
      msg.includes("[tv] disconnected") ||
      msg.includes("Script error") ||
      msg.includes("Cannot listen to the event from the provided iframe") ||
      msg.includes("contentWindow")
    ) return;
    originalError.apply(console, args);
  };
  window.addEventListener("error", (e) => {
    const msg = e.message || '';
    if (
      msg.includes("ResizeObserver loop") ||
      msg.includes("[tv] disconnected") ||
      msg.includes("Script error") ||
      msg.includes("Cannot listen to the event") ||
      msg.includes("contentWindow")
    ) {
      const overlay = document.getElementById("webpack-dev-server-client-overlay");
      if (overlay) overlay.style.display = "none";
      const overlayDiv = document.getElementById("webpack-dev-server-client-overlay-div");
      if (overlayDiv) overlayDiv.style.display = "none";
      e.stopImmediatePropagation();
    }
  });
  const removeInjectedScripts = () => {
    document.querySelectorAll('script').forEach((s) => {
      if (s.src.includes('doubleclick') || s.src.includes('aframe')) s.remove();
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeInjectedScripts);
  } else {
    removeInjectedScripts();
  }
}

const isMaintenance = false;
const wasRecoveryLink =
  typeof window !== 'undefined' &&
  (window.location.href.includes('type=recovery') || window.location.hash.includes('type=recovery'));
if (wasRecoveryLink) Cookies.set("xfolyo_recovery_lock", "true", { expires: 1 });

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

const hasStoredSupabaseSession = () => {
  if (typeof window === "undefined" || wasRecoveryLink) return true;
  try {
    return Object.keys(window.localStorage).some((key) =>
      key.startsWith("sb-") && key.endsWith("-auth-token") && Boolean(window.localStorage.getItem(key))
    );
  } catch {
    return true;
  }
};

const formatTx = (d) => {
  if (!d) return {};
  const id_stock = d.id_stock || d.ID_Stock || d.symbol || "";
  return {
    id: d.id,
    ID_Stock: id_stock,
    Type: d.type || d.Type || "",
    Date_Transaction: d.date_transaction || d.Date_Transaction || d.date || "",
    Amount_Stock: d.amount_stock ?? d.Amount_Stock ?? d.shares ?? 0,
    Price: d.price ?? d.Price ?? 0,
    Total_Amount: d.total_amount ?? d.Total_Amount ?? 0,
    FX_Rate: d.fx_rate ?? d.FX_Rate ?? 0,
    Commission: d.commission ?? d.Commission ?? d.Comm ?? 0,
    VAT: d.vat ?? d.VAT ?? 0,
    SEC_Fee: d.sec_fee ?? d.SEC_Fee ?? d.SECFee ?? 0,
    TAF_Fee: d.taf_fee ?? d.TAF_Fee ?? d.TAFFee ?? 0,
    symbol: id_stock,
    Gross_Amount: d.gross_amount ?? d.Gross_Amount ?? 0,
    gross_amount: d.gross_amount ?? d.Gross_Amount ?? 0,
  };
};
const formatDiv = (d) => ({
  id: d.id, ID_Stock: d.id_stock, Dividend_Amount: d.dividend_amount,
  WHT_15: d.wht_15, Total_Amount: d.total_amount, Date_Dividend: d.date_dividend,
});
const formatCap = (d) => ({
  id: d.id, Type: d.type, Amount_USD: d.amount_usd, Date_Transaction: d.date_transaction,
});

const FullPageLoader = ({ isDarkMode = false }) => (
  <div className={`xfolyo-page-loader h-screen flex flex-col gap-4 justify-center items-center ${isDarkMode ? "xfolyo-page-loader-dark" : ""}`} role="status" aria-live="polite">
    <div className="xfolyo-page-loader-mark"><TrendingUp size={21} aria-hidden="true" /></div>
    <div className="flex items-center gap-2 text-sm font-semibold"><Loader2 className="animate-spin" size={16} aria-hidden="true" /> กำลังเปิด XFOLYO</div>
  </div>
);

const MaintenancePage = () => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center font-sans">
    <div className="relative mb-8">
      <div className="w-24 h-24 bg-sky-500/10 rounded-[2.5rem] flex items-center justify-center animate-pulse">
        <ShieldAlert className="text-sky-500" size={48} />
      </div>
    </div>
    <h1 className="text-5xl font-black text-slate-900 uppercase tracking-tighter mb-4 leading-tight">
      ปิดปรับปรุงชั่วคราว <br />
      <span className="text-sky-600 text-2xl sm:text-4xl">Maintenance</span>
    </h1>
    <p className="max-w-md text-slate-600 text-sm font-bold leading-relaxed mb-10">
      "ขออภัยในความไม่สะดวกครับ ขณะนี้ระบบ XFOLYO อยู่ในระหว่างการอัปเกรดระบบความปลอดภัย"
    </p>
  </div>
);

export default function App() {
  if (isMaintenance) return <MaintenancePage />;

  const [currentTheme, setCurrentTheme] = useState(
    () => {
      const savedTheme = localStorage.getItem("xfolyo_theme");
      return savedTheme && THEMES[savedTheme] ? savedTheme : "MODERN";
    }
  );
  const themeConfig = useMemo(() => THEMES[currentTheme] || THEMES.MODERN, [currentTheme]);
  const isDarkMode = useMemo(() => themeConfig.isDark ?? false, [themeConfig]);

  useEffect(() => {
    localStorage.setItem("xfolyo_theme", currentTheme);
  }, [currentTheme]);

  const handleThemeChange = useCallback((newTheme) => {
    if (THEMES[newTheme]) {
      setCurrentTheme(newTheme);
      localStorage.setItem("xfolyo_theme", newTheme);
    }
  }, []);
  const handleDarkToggle = useCallback(() => {
    const paired = DARK_TOGGLE_MAP[currentTheme];
    if (paired && paired !== currentTheme) handleThemeChange(paired);
  }, [currentTheme, handleThemeChange]);
  const hasDarkToggle = useMemo(
    () => DARK_TOGGLE_MAP[currentTheme] && DARK_TOGGLE_MAP[currentTheme] !== currentTheme,
    [currentTheme]
  );

  const [lang, setLang] = useState(() => localStorage.getItem("xfolyo_lang") || "TH");
  const t = TRANSLATIONS[lang] || TRANSLATIONS.TH;
  useEffect(() => { localStorage.setItem("xfolyo_lang", lang); }, [lang]);

  // ── Initial tab resolution (handles /admin/* deep links) ─────────────────
  const getInitialInfo = useCallback(() => {
    if (typeof window === 'undefined')
      return { tab: 'summary', showAuth: false, authMode: 'login', initialRecovery: false };
    const path = window.location.pathname.replace(/^\//, '');
    const topSegment = path.split('/')[0];
    const isRecovering =
      wasRecoveryLink || path.includes('update-password') ||
      path.includes('reset-password') || Cookies.get('xfolyo_recovery_lock') === 'true';
    const validTabs = ['summary','allocation','trades','capital','dividends','settled','holdings','xd','fundamental','pivot','tax','privacy','terms','status','admin'];
    const resolvedTab = topSegment === 'admin' ? 'admin'
      : validTabs.includes(topSegment) && !isRecovering ? topSegment : 'summary';
    return {
      tab: resolvedTab,
      showAuth: ['login','register','status'].includes(topSegment) || isRecovering,
      authMode: topSegment === 'register' ? 'register' : isRecovering ? 'update-password' : 'login',
      initialRecovery: isRecovering,
    };
  }, []);

  const [initialInfo] = useState(getInitialInfo);
  const [activeTab, setActiveTab] = useState(initialInfo.tab);
  const [showAuth, setShowAuth] = useState(initialInfo.showAuth);
  const [authMode, setAuthMode] = useState(initialInfo.authMode);
  const [isRecovery, setIsRecovery] = useState(initialInfo.initialRecovery);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!isMobileNavOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsMobileNavOpen(false);
    };
    const closeOnDesktop = () => {
      if (window.innerWidth > 900) setIsMobileNavOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnDesktop);
    };
  }, [isMobileNavOpen]);

  // ── Track which tabs have been visited so we don't unmount them ───────────
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([initialInfo.tab]));
  const handleTabChange = useCallback((tabId) => {
    setIsMobileNavOpen(false);
    startTransition(() => {
      setActiveTab(tabId);
      setVisitedTabs(prev => {
        if (prev.has(tabId)) return prev;
        const next = new Set(prev);
        next.add(tabId);
        return next;
      });
    });
  }, []);

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({ isPro: false, isTrial: true, proExpireDate: null, expiryWarningRead: false });
  const [role, setRole] = useState("User");
  const [avatarUrl, setAvatarUrl] = useState(null);

  const [transactionsMap, setTransactionsMap] = useState(new Map());
  const [dividends, setDividends] = useState([]);
  const [capital, setCapital] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [dividendPatterns, setDividendPatterns] = useState(
    () => readFreshCache(DIVIDEND_PATTERNS_CACHE_KEY, DIVIDEND_PATTERNS_CACHE_TTL_MS) || []
  );
  const [marketDataStore, setMarketDataStore] = useState(
    () => readFreshCache(MARKET_SNAPSHOT_CACHE_KEY, MARKET_SNAPSHOT_CACHE_TTL_MS)?.prices || {}
  );
  const [usdThb, setUsdThb] = useState(
    () => readFreshCache(PUBLIC_FX_CACHE_KEY, PUBLIC_FX_CACHE_TTL_MS)?.rate || 34.5
  );
  const [hasLiveFxRate, setHasLiveFxRate] = useState(
    () => Boolean(readFreshCache(PUBLIC_FX_CACHE_KEY, PUBLIC_FX_CACHE_TTL_MS)?.rate)
  );
  const [lastSync, setLastSync] = useState(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [notification, setNotification] = useState(null);
  // Public visitors can see the landing page immediately while the existing
  // auth check continues in the background. Stored sessions still keep the
  // loader to avoid briefly showing the public page to signed-in users.
  const [isLoading, setIsLoading] = useState(hasStoredSupabaseSession);

  const [dataReady, setDataReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [modal, setModal] = useState({ type: null, editing: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isFetching = useRef(false);
  const isFetchingData = useRef(false);
  const lastProcessedUid = useRef(null);
  const lastVisibilityFetch = useRef(0);
  const marketStarted = useRef(false);
  const dividendPatternsLoaded = useRef(dividendPatterns.length > 0);

  const transactions = useMemo(() => [...transactionsMap.values()], [transactionsMap]);

  const holdingsSummary = useMemo(() => {
    const h = {};
    transactions.forEach((d) => {
      if (!d.ID_Stock) return;
      if (!h[d.ID_Stock]) h[d.ID_Stock] = 0;
      const isAdd =
        d.Type === "Buy" || d.Type === "ซื้อ" ||
        d.Type?.toLowerCase().includes("adjust") ||
        d.Type?.toLowerCase().includes("split");
      h[d.ID_Stock] += (isAdd ? 1 : -1) * Number(d.Amount_Stock || 0);
    });
    return Object.fromEntries(Object.entries(h).filter(([, v]) => v > 0.0000001));
  }, [transactions]);

  const isMasterAdmin = Boolean(
    user?.email && ADMIN_EMAIL &&
    user.email.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase().trim()
  );
  const isSystemAdminOnly = role === "System Admin" || isMasterAdmin;
  const isAdminRole = isSystemAdminOnly || role === "Checker";
  const expire = profile?.proExpireDate ? new Date(profile.proExpireDate) : null;
  const isPro = isAdminRole ? true : (expire && expire > new Date());
  const isReadOnly = false;

  const TabSkeleton = useCallback(() => {
    switch (activeTab) {
      case "summary":     return <SummarySkeleton isDarkMode={isDarkMode} />;
      case "allocation":  return <AllocationSkeleton isDarkMode={isDarkMode} />;
      case "trades":      return <TransactionSkeleton isDarkMode={isDarkMode} />;
      case "capital":     return <CapitalSkeleton isDarkMode={isDarkMode} />;
      case "dividends":   return <DividendSkeleton isDarkMode={isDarkMode} />;
      case "settled":     return <RealizedPLSkeleton isDarkMode={isDarkMode} />;
      case "holdings":    return <HoldingSkeleton isDarkMode={isDarkMode} />;
      case "xd":          return <XDSkeleton isDarkMode={isDarkMode} />;
      case "fundamental": return <FundamentalSkeleton isDarkMode={isDarkMode} />;
      case "insights":    return <InsightsSkeleton isDarkMode={isDarkMode} />;
      default:            return <SummarySkeleton isDarkMode={isDarkMode} />;
    }
  }, [activeTab, isDarkMode]);

  // ── Market data: start only after data is ready, defer 2s ────────────────
  const applyMarketSnapshot = useCallback((snapshot, syncedAt = null) => {
    if (!snapshot || typeof snapshot !== 'object') return false;
    const nextStore = {};
    Object.entries(snapshot).forEach(([symbol, item]) => {
      if (!item || typeof item !== 'object') return;
      const price = Number(item.price);
      const change = Number(item.change);
      nextStore[symbol] = {
        price: Number.isFinite(price) ? price : 0,
        change: Number.isFinite(change) ? change : 0,
        instrument_type: item.instrument_type,
      };
    });
    if (Object.keys(nextStore).length === 0) return false;

    setMarketDataStore(nextStore);
    writeCache(MARKET_SNAPSHOT_CACHE_KEY, { prices: nextStore, updatedAt: syncedAt });
    if (nextStore.USDTHB?.price) {
      setUsdThb(nextStore.USDTHB.price);
      setHasLiveFxRate(true);
      writeCache(PUBLIC_FX_CACHE_KEY, { rate: nextStore.USDTHB.price });
    }
    setLastSync(
      syncedAt
        ? new Date(syncedAt).toLocaleTimeString('en-GB')
        : new Date().toLocaleTimeString('en-GB')
    );
    return true;
  }, []);

  const fetchMarketData = useCallback(async (force = false) => {
    if (document.hidden || isFetching.current) return;
    if (!force) {
      const cached = readFreshCache(MARKET_SNAPSHOT_CACHE_KEY, MARKET_SNAPSHOT_CACHE_TTL_MS);
      if (cached?.prices && applyMarketSnapshot(cached.prices, cached.updatedAt)) return;
    }
    isFetching.current = true;
    try {
      const { data: snapshotRow, error: snapshotError } = await withTimeout(
        supabase
          .from('market_price_snapshots')
          .select('prices, updated_at')
          .eq('snapshot_key', 'current')
          .maybeSingle()
      );
      if (!snapshotError && applyMarketSnapshot(snapshotRow?.prices, snapshotRow?.updated_at)) {
        return;
      }

      // Deployment fallback: retain price loading while the snapshot migration
      // is being applied or if its singleton row is temporarily unavailable.
      const { data, error } = await withTimeout(
        supabase.from('prices').select('symbol, price, change, instrument_type')
      );
      if (!error && data) {
        const fallbackSnapshot = Object.fromEntries(
          data.map((item) => [item.symbol, item])
        );
        applyMarketSnapshot(fallbackSnapshot);
      }
    } catch (err) {
      console.error("Market fetch error:", err);
    } finally {
      isFetching.current = false;
    }
  }, [applyMarketSnapshot]);

  useEffect(() => {
    if (!dataReady || marketStarted.current) return undefined;
    marketStarted.current = true;

    const firstFetch = window.setTimeout(() => fetchMarketData(false), 2000);
    const channel = supabase
      .channel('market-price-snapshot-live')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'market_price_snapshots',
          filter: 'snapshot_key=eq.current',
        },
        (payload) => {
          applyMarketSnapshot(payload.new?.prices, payload.new?.updated_at);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') fetchMarketData(true);
      });

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') fetchMarketData(false);
    };
    const refreshWhenOnline = () => fetchMarketData(true);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('online', refreshWhenOnline);

    return () => {
      window.clearTimeout(firstFetch);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenOnline);
      supabase.removeChannel(channel);
      marketStarted.current = false;
    };
  }, [applyMarketSnapshot, dataReady, fetchMarketData]);

  // Keep the public preview aligned with the workspace USD/THB source while
  // loading only the one market record needed by the landing page.
  useEffect(() => {
    if (user || isLoading) return undefined;
    let active = true;
    const fetchPublicFxRate = async (force = false) => {
      if (document.hidden) return;
      if (!force) {
        const cached = readFreshCache(PUBLIC_FX_CACHE_KEY, PUBLIC_FX_CACHE_TTL_MS);
        const cachedRate = Number(cached?.rate);
        if (Number.isFinite(cachedRate) && cachedRate > 0) {
          setUsdThb(cachedRate);
          setHasLiveFxRate(true);
          return;
        }
      }
      try {
        const { data, error } = await withTimeout(
          supabase.from('prices').select('price').eq('symbol', 'USDTHB').maybeSingle()
        );
        let nextRate = !error ? Number(data?.price) : Number.NaN;
        if ((!Number.isFinite(nextRate) || nextRate <= 0) && MARKET_PROXY_URL) {
          const response = await withTimeout(
            fetch(`${MARKET_PROXY_URL}?symbol=${encodeURIComponent('USDTHB=X')}&range=1d&interval=1m`)
          );
          if (response.ok) {
            const payload = await response.json();
            nextRate = Number(payload?.chart?.result?.[0]?.meta?.regularMarketPrice);
          }
        }
        if (!active || !Number.isFinite(nextRate) || nextRate <= 0) return;
        setUsdThb(nextRate);
        setHasLiveFxRate(true);
        writeCache(PUBLIC_FX_CACHE_KEY, { rate: nextRate });
        setLastSync(new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }));
      } catch (error) {
        console.info('Public FX rate unavailable; landing preview is using sample data.', error);
      }
    };
    fetchPublicFxRate(false);
    const interval = window.setInterval(() => fetchPublicFxRate(true), PUBLIC_FX_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [user, isLoading]);

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, [notification]);

  useEffect(() => {
    if (!user && showAuth) {
      document.title = authMode === "register"
        ? "สมัครสมาชิก | XFOLYO"
        : authMode === "update-password"
          ? "ตั้งรหัสผ่านใหม่ | XFOLYO"
          : "เข้าสู่ระบบ | XFOLYO";
      return;
    }
    const titles = {
      summary: `${t.summary || "Summary"} | XFOLYO`,
      holdings: `${t.holdings || "Holdings"} | XFOLYO`,
      trades: `${t.trades || "Trades"} | XFOLYO`,
      capital: `${t.capital || "Capital"} | XFOLYO`,
      dividends: `${t.dividends || "Dividends"} | XFOLYO`,
      settled: `${t.settled || "Settled"} | XFOLYO`,
      allocation: `${t.allocation || "Allocation"} | XFOLYO`,
      xd: `${t.xd || "XD.Calendar"} | XFOLYO`,
      fundamental: `${t.fundamental || "Fundamentals"} | XFOLYO`,
      admin: "Admin | XFOLYO",
      privacy: "Privacy Policy | XFOLYO",
      terms: "Terms of Service | XFOLYO",
      status: "สถานะระบบ | XFOLYO",
      tax: `${lang === "EN" ? "Tax Estimate" : "ประมาณการภาษี"} | XFOLYO`,
      pivot: `${lang === "EN" ? "Pivot Levels" : "แนวรับ–แนวต้าน"} | XFOLYO`,
    };
    document.title = titles[activeTab]
      || (user ? "XFOLYO" : "XFOLYO | รู้กำไรจริงของพอร์ตหุ้นสหรัฐฯ");
  }, [activeTab, user, showAuth, authMode, lang, t]);

  // ── URL sync ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoading) return;
    let targetPath = '/';
    if (isRecovery) {
      targetPath = '/update-password';
    } else if (!user) {
      targetPath = showAuth ? `/${authMode}` : ['privacy', 'terms'].includes(activeTab) ? `/${activeTab}` : '/';
    } else {
      if (['privacy', 'terms', 'status'].includes(activeTab)) {
        targetPath = `/${activeTab}`;
      } else if (activeTab === 'admin') {
        const currentPath = window.location.pathname;
        if (!currentPath.startsWith('/admin')) targetPath = '/admin/slip';
        else return;
      } else {
        targetPath = `/${activeTab}`;
      }
    }
    if (window.location.pathname !== targetPath)
      window.history.pushState(null, '', targetPath);
  }, [activeTab, user, showAuth, authMode, isLoading, isRecovery]);

  // ── Popstate ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handlePopState = () => {
      const fullPath = window.location.pathname.replace(/^\//, '');
      const topSegment = fullPath.split('/')[0];
      const isRecovering =
        topSegment.includes('update-password') || topSegment.includes('reset-password') ||
        Cookies.get('xfolyo_recovery_lock') === 'true' ||
        window.location.hash.includes('type=recovery');
      if (['login','register'].includes(topSegment) || isRecovering) {
        setShowAuth(true);
        setAuthMode(topSegment === 'register' ? 'register' : isRecovering ? 'update-password' : 'login');
        setIsRecovery(isRecovering);
      } else if (topSegment === '' || topSegment === 'summary') {
        if (!isRecovering) { setShowAuth(false); setIsRecovery(false); handleTabChange('summary'); }
      } else if (topSegment === 'admin') {
        if (!isRecovering) { setShowAuth(false); setIsRecovery(false); handleTabChange('admin'); }
      } else {
        if (!isRecovering) { setShowAuth(false); setIsRecovery(false); handleTabChange(topSegment); }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handleTabChange]);

  const loadUserProfile = useCallback(async (u) => {
    if (!u) return;
    try {
      const masterEmail = ADMIN_EMAIL ? ADMIN_EMAIL.toLowerCase().trim() : null;
      const isMaster = masterEmail && u.email.toLowerCase().trim() === masterEmail;
      const [{ data: profileData }, { data: teamData }] = await withTimeout(
        Promise.all([
          supabase.from('profiles').select('id, is_pro, is_trial, pro_expire_date, expiry_warning_read, avatar_url, role').eq('id', u.id).maybeSingle(),
          isMaster ? Promise.resolve({ data: null }) : supabase.from('team_roles').select('role').ilike('email', u.email.trim()).maybeSingle(),
        ])
      );
      if (profileData) {
        setProfile({
          isPro: profileData.is_pro || false,
          isTrial: profileData.is_trial !== undefined ? profileData.is_trial : false,
          proExpireDate: profileData.pro_expire_date || null,
          expiryWarningRead: profileData.expiry_warning_read || false,
        });
        if (profileData.avatar_url) setAvatarUrl(profileData.avatar_url);
      } else {
        setProfile({ isPro: false, isTrial: true, proExpireDate: null, expiryWarningRead: false });
      }
      let secureRole = "User";
      if (isMaster) secureRole = "System Admin";
      else if (teamData && (teamData.role === "System Admin" || teamData.role === "Checker")) secureRole = teamData.role;
      else if (profileData && (profileData.role === "System Admin" || profileData.role === "Checker")) {
        secureRole = "User";
        await supabase.from('profiles').update({ role: 'User' }).eq('id', u.id);
      } else if (profileData && profileData.role) secureRole = profileData.role;
      setRole(secureRole);
    } catch (err) {
      console.error("Profile Error:", err);
    }
  }, []);

  const fetchCycle = useCallback(async (uid) => {
    if (!uid || isFetchingData.current) return;
    isFetchingData.current = true;
    try {
      const txRes = await withTimeout(
        supabase
          .from('transactions')
          .select('id, id_stock, type, date_transaction, amount_stock, price, total_amount, fx_rate, commission, vat, sec_fee, taf_fee, gross_amount')
          .eq('user_id', uid)
      );
      if (txRes.data) setTransactionsMap(new Map(txRes.data.map((d) => [d.id, formatTx(d)])));
    } catch (err) {
      console.error("Transactions fetch error:", err);
    } finally {
      setDataReady(true);
    }

    try {
      const [divRes, capRes, notiRes] = await Promise.allSettled([
        withTimeout(supabase.from('dividends').select('id, id_stock, dividend_amount, wht_15, total_amount, date_dividend').eq('user_id', uid)),
        withTimeout(supabase.from('capitals').select('id, type, amount_usd, date_transaction').eq('user_id', uid)),
        withTimeout(
          supabase
            .from('notifications')
            .select('id, user_id, message, is_read, created_at')
            .eq('user_id', uid)
            .order('created_at', { ascending: false })
            .limit(50)
        ),
      ]);
      if (divRes.status === 'fulfilled' && divRes.value.data)     setDividends(divRes.value.data.map(formatDiv));
      if (capRes.status === 'fulfilled' && capRes.value.data)     setCapital(capRes.value.data.map(formatCap));
      if (notiRes.status === 'fulfilled' && notiRes.value.data)   setNotifications(notiRes.value.data);
    } catch (err) {
      console.error("Background fetch error:", err);
    } finally {
      isFetchingData.current = false;
    }
  }, []);

  // Dividend patterns are only needed by XD Calendar. Most sessions never open
  // that tab, so defer the shared lookup and reuse it for one day per browser.
  useEffect(() => {
    if (activeTab !== 'xd' || !user?.uid || dividendPatternsLoaded.current) return;
    let active = true;
    const loadDividendPatterns = async () => {
      const cached = readFreshCache(DIVIDEND_PATTERNS_CACHE_KEY, DIVIDEND_PATTERNS_CACHE_TTL_MS);
      if (cached) {
        if (active) setDividendPatterns(cached);
        dividendPatternsLoaded.current = true;
        return;
      }
      try {
        const { data, error } = await withTimeout(
          supabase.from('dividend_patterns').select('symbol, frequency')
        );
        if (error) throw error;
        const patterns = data || [];
        writeCache(DIVIDEND_PATTERNS_CACHE_KEY, patterns);
        dividendPatternsLoaded.current = true;
        if (active) setDividendPatterns(patterns);
      } catch (error) {
        console.error('Dividend patterns fetch error:', error);
      }
    };
    loadDividendPatterns();
    return () => { active = false; };
  }, [activeTab, user?.uid]);

  // ── Realtime subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;
    const uid = user.uid;
    const channel = supabase
      .channel(`user-data-${uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions', filter: `user_id=eq.${uid}` }, (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE')
          setTransactionsMap((prev) => new Map(prev).set(payload.new.id, formatTx(payload.new)));
        if (payload.eventType === 'DELETE')
          setTransactionsMap((prev) => { const n = new Map(prev); n.delete(payload.old.id); return n; });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dividends', filter: `user_id=eq.${uid}` }, (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE')
          setDividends((prev) => { const f = prev.filter((d) => d.id !== payload.new.id); return [...f, formatDiv(payload.new)]; });
        if (payload.eventType === 'DELETE')
          setDividends((prev) => prev.filter((d) => d.id !== payload.old.id));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'capitals', filter: `user_id=eq.${uid}` }, (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE')
          setCapital((prev) => { const f = prev.filter((c) => c.id !== payload.new.id); return [...f, formatCap(payload.new)]; });
        if (payload.eventType === 'DELETE')
          setCapital((prev) => prev.filter((c) => c.id !== payload.old.id));
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.uid]);

  // ── Visibility refetch (with cooldown) ────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;
    const COOLDOWN = 5 * 60 * 1000;
    const onVisible = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastVisibilityFetch.current < COOLDOWN) return;
      lastVisibilityFetch.current = now;
      fetchCycle(user.uid);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.uid, fetchCycle]);

  // ── Auth init ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    if (wasRecoveryLink) {
      Cookies.set("xfolyo_recovery_lock", "true", { expires: 1 });
      setIsRecovery(true);
    }

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await withTimeout(supabase.auth.getSession());
        if (error) { await supabase.auth.signOut(); throw error; }
        if (session?.user) {
          const u = session.user;
          lastProcessedUid.current = u.id;
          if (isMounted) setUser({ uid: u.id, email: u.email });
          Cookies.set("xfolyo_user", JSON.stringify({ uid: u.id, email: u.email }), { expires: 7 });

          await Promise.allSettled([loadUserProfile(u), fetchCycle(u.id)]);
        } else {
          if (isMounted) setUser(null);
          Cookies.remove("xfolyo_user");
        }
      } catch (err) {
        console.error("Auth System Error:", err);

        if (err?.message !== "TIMEOUT") {
          await supabase.auth.signOut();
          if (isMounted) setUser(null);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      if (_event === 'INITIAL_SESSION') return;
      if (_event === 'TOKEN_REFRESHED' && !session) {
        window.setTimeout(() => { void supabase.auth.signOut(); }, 0);
        return;
      }
      if (_event === 'PASSWORD_RECOVERY') {
        Cookies.set("xfolyo_recovery_lock", "true", { expires: 1 });
        setIsRecovery(true); setShowAuth(true); setAuthMode('update-password');
      } else if (_event === 'USER_UPDATED') {
        Cookies.remove("xfolyo_recovery_lock");
        setIsRecovery(false); setShowAuth(false); setActiveTab("summary");
        setNotification("อัปเดตรหัสผ่านสำเร็จ!");
      } else if (_event === 'SIGNED_OUT') {
        Cookies.remove("xfolyo_recovery_lock");
        lastProcessedUid.current = null;
        setIsRecovery(false); setRole("User");
        setProfile({ isPro: false, isTrial: true, proExpireDate: null, expiryWarningRead: false });
        setTransactionsMap(new Map()); setDividends([]); setCapital([]);
        setUser(null); setDataReady(false);
        setVisitedTabs(new Set(['summary']));
        Cookies.remove("xfolyo_user");
        return;
      }
      if (session?.user) {
        const u = session.user;
        if (lastProcessedUid.current === u.id && _event !== 'SIGNED_IN') return;
        lastProcessedUid.current = u.id;
        setUser({ uid: u.id, email: u.email });
        Cookies.set("xfolyo_user", JSON.stringify({ uid: u.id, email: u.email }), { expires: 7 });
        if (_event === 'SIGNED_IN') {
          window.setTimeout(() => {
            if (isMounted) void Promise.allSettled([loadUserProfile(u), fetchCycle(u.id)]);
          }, 0);
        }
      } else {
        lastProcessedUid.current = null;
        setUser(null); setDataReady(false);
        Cookies.remove("xfolyo_user");
      }
    });

    return () => { isMounted = false; if (subscription) subscription.unsubscribe(); };
  }, [loadUserProfile, fetchCycle]);

  const updateLocalData = useCallback((tableName, newItem) => {
    if (tableName === 'transactions') {
      const f = formatTx(newItem);
      setTransactionsMap((prev) => new Map(prev).set(f.id, f));
    } else if (tableName === 'dividends') {
      const f = formatDiv(newItem);
      setDividends((prev) => { const filtered = prev.filter((d) => d.id !== f.id); return [...filtered, f]; });
    } else if (tableName === 'capitals') {
      const f = formatCap(newItem);
      setCapital((prev) => { const filtered = prev.filter((c) => c.id !== f.id); return [...filtered, f]; });
    }
  }, []);

  const handleAddClick = (type) => setModal({ type, editing: null });
  const handleEditClick = (type, item) => setModal({ type, editing: item });
  const handleDeleteClick = (col, id) => setDeleteTarget({ col, id });
  const checkLimit = () => true;
  const openSymbolSearch = useCallback(() => {
    const symbol = searchTerm.trim().replace(/^\$/, "").toUpperCase();
    if (!symbol) return;
    const normalized = symbol.includes(":") ? symbol : `NASDAQ:${symbol}`;
    localStorage.setItem("xfolyo_last_symbol", normalized);
    handleTabChange("fundamental");
    window.setTimeout(() => window.dispatchEvent(new CustomEvent("xfolyo:symbol-search", { detail: { symbol: normalized } })), 0);
    setIsMobileSearchOpen(false);
  }, [searchTerm, handleTabChange]);
  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const tableMap = { Transactions: "transactions", Dividend: "dividends", Capital: "capitals" };
      const tableName = tableMap[deleteTarget.col];
      if (!tableName) throw new Error("UNKNOWN_TABLE");
      const { data: deletedRows, error } = await withTimeout(
        supabase.from(tableName).delete().eq('id', deleteTarget.id).select('id')
      );
      if (error) throw error;
      if (!deletedRows?.length) throw new Error('DELETE_NOT_CONFIRMED');
      if (tableName === 'transactions') setTransactionsMap((prev) => { const next = new Map(prev); next.delete(deleteTarget.id); return next; });
      if (tableName === 'dividends') setDividends((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      if (tableName === 'capitals') setCapital((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setNotification("ลบข้อมูลสำเร็จ");
      setDeleteTarget(null);
    } catch {
      setNotification("ลบไม่สำเร็จ ข้อมูลยังอยู่ครบ กรุณาลองอีกครั้ง");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) return <FullPageLoader isDarkMode={isDarkMode} />;

  if (isRecovery) {
    return (
      <Suspense fallback={<FullPageLoader isDarkMode={isDarkMode} />}>
        <TradingViewTicker isDarkMode={isDarkMode} />
        <LoginScreen
          isDarkMode={isDarkMode}
          initialMode="update-password"
          onModeChange={setAuthMode}
          onBack={() => {
            if (Cookies.get("xfolyo_recovery_lock") === "true") {
              supabase.auth.signOut();
              Cookies.remove("xfolyo_recovery_lock");
              setIsRecovery(false); setShowAuth(false);
              window.location.href = "/";
            } else {
              setIsRecovery(false); setShowAuth(false);
            }
          }}
        />
      </Suspense>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<FullPageLoader isDarkMode={isDarkMode} />}>
        {showAuth
          ? <LoginScreen isDarkMode={isDarkMode} onBack={() => setShowAuth(false)} initialMode={authMode} onModeChange={setAuthMode} />
          : activeTab === "privacy"
            ? <Privacy isDarkMode={isDarkMode} onBack={() => { setActiveTab("summary"); window.scrollTo(0, 0); }} />
            : activeTab === "terms"
              ? <Terms isDarkMode={isDarkMode} onBack={() => { setActiveTab("summary"); window.scrollTo(0, 0); }} />
              : <><TradingViewTicker isDarkMode={isDarkMode} /><LandingPage onGoToAuth={() => setShowAuth(true)} isDarkMode={isDarkMode} setIsDarkMode={() => handleDarkToggle()} usdThbRate={usdThb} usdThbUpdatedAt={lastSync} hasLiveFxRate={hasLiveFxRate} onGoToPrivacy={() => { setActiveTab("privacy"); window.scrollTo(0, 0); }} onGoToTerms={() => { setActiveTab("terms"); window.scrollTo(0, 0); }} /><CookieBanner isDarkMode={isDarkMode} onGoToPrivacy={() => { setActiveTab("privacy"); window.scrollTo(0, 0); }} /></>
        }
      </Suspense>
    );
  }

  // ── Common props passed to all tabs ───────────────────────────────────────
  const commonTabProps = { isDarkMode, t, themeConfig };
  const isEditorialTheme = currentTheme === "MODERN" || currentTheme === "MODERN_DARK";
  const isEditorialDark = currentTheme === "MODERN_DARK";
  const dashboardNavItems = [
    { id: "summary",     l: t.summary     || "สรุปพอร์ต", icon: LayoutDashboard, group: "พอร์ต" },
    { id: "holdings",    l: t.holdings    || "หุ้นที่ถือ", icon: BriefcaseBusiness, group: "พอร์ต" },
    { id: "trades",      l: t.trades      || "รายการซื้อขาย", icon: ArrowLeftRight, group: "รายการ" },
    { id: "capital",     l: t.capital     || "เงินทุน", icon: Landmark, group: "รายการ" },
    { id: "dividends",   l: t.dividends   || "เงินปันผล", icon: DollarSign, group: "รายการ" },
    { id: "settled",     l: t.settled     || "กำไรที่รับรู้แล้ว", icon: CircleCheckBig, group: "รายการ" },
    { id: "allocation",  l: t.allocation  || "สัดส่วนพอร์ต", icon: PieChart, group: "วิเคราะห์" },
    { id: "xd",          l: t.xd          || "ปฏิทินปันผล", icon: CalendarDays, group: "วิเคราะห์" },
    { id: "pivot",       l: t.pivot       || (lang === "EN" ? "Pivot Levels" : "แนวรับ–แนวต้าน"), icon: TableProperties, group: "วิเคราะห์" },
    { id: "fundamental", l: t.fundamental || "ข้อมูลพื้นฐาน", icon: LineChart, group: "วิเคราะห์" },
    { id: "tax",         l: t.tax_calculator || (lang === "EN" ? "Tax Estimate" : "ประมาณการภาษี"), icon: ReceiptText, group: "วิเคราะห์" },
  ];
  const activeNavItem = dashboardNavItems.find((item) => item.id === activeTab);

  return (
    <div className={`h-screen flex flex-col transition-all duration-300 ${themeConfig.bg} ${themeConfig.text} ${themeConfig.fontClass} ${isEditorialTheme ? `editorial-shell ${isEditorialDark ? "editorial-shell-dark" : ""}` : ""}`}>
      <div className={isEditorialTheme ? "editorial-ticker" : ""}>
        <Suspense fallback={null}>
          <TradingViewTicker isDarkMode={isDarkMode} />
        </Suspense>
      </div>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className={`${isEditorialTheme ? "editorial-header" : "p-3 sm:p-4 border-b shadow-sm sticky top-0"} flex items-center justify-between z-40 ${themeConfig.header}`}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
          {isEditorialTheme && (
            <button
              type="button"
              className="editorial-menu-button"
              onClick={() => setIsMobileNavOpen(true)}
              aria-label="เปิดเมนูพอร์ต"
              aria-expanded={isMobileNavOpen}
              aria-controls="portfolio-navigation"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          )}
          <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#0f172a] flex items-center justify-center shadow-lg">
            <TrendingUp className="text-sky-400" size={20} />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="text-base sm:text-xl font-black uppercase tracking-tighter truncate">XFOLYO</h1>
              {isPro
                ? <Coffee size={20} className="text-amber-600 shrink-0" aria-label="Supporter plan" />
                : <span className="shrink-0 text-[11px] sm:text-[11px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-black leading-none">FREE MEMBER</span>
              }
            </div>
            <p className="text-[11px] sm:text-[11px] font-bold opacity-60 truncate">1 USD = {usdThb.toFixed(2)} THB</p>
          </div>
        </div>

        <div className={`flex items-center gap-1.5 sm:gap-3 shrink-0 ${isEditorialTheme ? "editorial-header-actions" : ""}`}>
          {!isPro && (
            <button onClick={() => setShowUpgradeModal(true)} className="editorial-support-button bg-gradient-to-r from-orange-400 to-amber-600 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-[11px] font-black shadow-lg transition-colors flex items-center gap-1.5">
              <span className="block sm:hidden"><Coffee size={12} /></span>
              <span className="hidden sm:flex items-center gap-1.5"><Coffee size={12} /> สนับสนุนค่ากาแฟ</span>
            </button>
          )}
          {!isReadOnly && (
            <div className="header-quick-add hidden md:flex">
              <button type="button" className="header-quick-add-button" onClick={() => setIsQuickAddOpen((value) => !value)} aria-expanded={isQuickAddOpen} aria-label={isQuickAddOpen ? "ปิดเมนูเพิ่มรายการ" : "เพิ่มรายการด่วน"}>
                {isQuickAddOpen ? <X size={17} /> : <Plus size={17} />}<span>เพิ่มรายการ</span>
              </button>
              {isQuickAddOpen && (
                <div className="header-quick-add-menu" role="menu" aria-label="เพิ่มรายการด่วน">
                  <button type="button" role="menuitem" onClick={() => { handleAddClick("trade"); setIsQuickAddOpen(false); }}>ซื้อหรือขายหุ้น</button>
                  <button type="button" role="menuitem" onClick={() => { handleAddClick("div"); setIsQuickAddOpen(false); }}>เงินปันผล</button>
                  <button type="button" role="menuitem" onClick={() => { handleAddClick("cap"); setIsQuickAddOpen(false); }}>เงินทุน</button>
                </div>
              )}
            </div>
          )}
          <div className={`editorial-search hidden md:flex relative w-48 lg:w-64 shadow-sm rounded-lg overflow-hidden border ${themeConfig.border} ${themeConfig.card.split(' ').filter(c => c.startsWith('bg-')).join(' ')}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" size={14} />
            <input
              type="search"
              aria-label="ค้นหาหุ้นเพื่อเปิดหน้าวิเคราะห์"
              placeholder={t.search_placeholder || "ค้นหาหุ้น เช่น TSM"}
              className="pl-9 pr-4 py-2 text-xs bg-transparent border-none outline-none w-full font-black"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                openSymbolSearch();
              }}
            />
          </div>
          <button type="button" onClick={() => setIsMobileSearchOpen((value) => !value)} aria-label="ค้นหาหุ้น" aria-expanded={isMobileSearchOpen} className="editorial-icon-button md:hidden p-2 rounded-lg border border-transparent">
            <Search size={18} aria-hidden="true" />
          </button>
          {hasDarkToggle && (
            <button type="button" aria-label={isDarkMode ? "ใช้โหมดสว่าง" : "ใช้โหมดมืด"} onClick={handleDarkToggle} className={`editorial-icon-button p-1.5 sm:p-2 rounded-lg border transition-colors ${isDarkMode ? "bg-slate-900 border-slate-700 hover:border-sky-500" : "bg-transparent border-transparent hover:bg-slate-100"}`}>
              {isDarkMode ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-slate-600" />}
            </button>
          )}
          <Suspense fallback={<div className="w-5 h-5"><Loader2 className="animate-spin text-slate-400" size={14} /></div>}>
            <NotificationBell notifications={notifications} profile={profile} isDarkMode={isDarkMode} userId={user?.uid} themeConfig={themeConfig} />
          </Suspense>
          <Suspense fallback={<div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse" />}>
            <ProfileDropdown user={user} profile={profile} isDarkMode={isDarkMode} onSignOut={() => {}} role={isSystemAdminOnly ? "System Admin" : role} hasUnlimitedScan={Boolean(isPro)} onUpgradeClick={() => setShowUpgradeModal(true)} lang={lang} setLang={setLang} onChangePasswordClick={() => setIsRecovery(true)} avatarUrl={avatarUrl} onAvatarChange={setAvatarUrl} setNotification={setNotification} onStatusClick={() => handleTabChange("status")} />
          </Suspense>
        </div>
      </header>
      {isMobileSearchOpen && (
        <form className={`mobile-symbol-search md:hidden ${themeConfig.header}`} onSubmit={(event) => { event.preventDefault(); openSymbolSearch(); }}>
          <Search size={17} aria-hidden="true" />
          <input autoFocus type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="ค้นหา Symbol เช่น TSM" aria-label="ค้นหา Symbol" />
          <button type="submit">เปิด Research</button>
        </form>
      )}

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      {isEditorialTheme && isMobileNavOpen && (
        <button type="button" className="editorial-nav-overlay" onClick={() => setIsMobileNavOpen(false)} aria-label="ปิดเมนูพอร์ต" />
      )}
      <nav id="portfolio-navigation" className={`${isEditorialTheme ? `editorial-nav ${isMobileNavOpen ? "editorial-nav-open" : ""}` : "flex gap-2 p-3 border-b overflow-x-auto no-scrollbar shadow-sm"} z-30 ${themeConfig.header}`}>
        {isEditorialTheme && (
          <>
            <div className="editorial-nav-mobile-head">
              <strong>Portfolio</strong>
              <button type="button" onClick={() => setIsMobileNavOpen(false)} aria-label="ปิดเมนูพอร์ต"><X size={20} aria-hidden="true" /></button>
            </div>
            <div className="editorial-nav-title">Portfolio</div>
            <div className="editorial-nav-label">Workspace</div>
          </>
        )}
        {["พอร์ต", "รายการ", "วิเคราะห์"].map((group) => (
          <React.Fragment key={group}>
            {isEditorialTheme && <div className="editorial-nav-label editorial-nav-group-label">{group}</div>}
            {dashboardNavItems.filter((item) => item.group === group).map((item) => (
              <button key={item.id} onClick={() => handleTabChange(item.id)} aria-current={activeTab === item.id ? "page" : undefined}
                className={`${isEditorialTheme ? "editorial-nav-item" : "whitespace-nowrap shrink-0 px-6 py-2 text-[11px] font-black uppercase"} transition-all ${themeConfig.radius} ${activeTab === item.id ? themeConfig.navActive : themeConfig.navInactive}`}>
                {isEditorialTheme && <item.icon size={17} strokeWidth={1.8} aria-hidden="true" />}
                {item.l}
              </button>
            ))}
          </React.Fragment>
        ))}
        {isAdminRole && (
          <button onClick={() => handleTabChange("admin")}
            className={`${isEditorialTheme ? "editorial-nav-item editorial-admin-item" : "whitespace-nowrap shrink-0 px-6 py-2 text-[11px] font-black uppercase ml-auto"} flex items-center gap-1 transition-all ${themeConfig.radius} ${
              activeTab === "admin"
                ? "bg-indigo-600 text-white shadow-md"
                : isDarkMode ? "text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30"
                             : "text-indigo-500 bg-indigo-50/50 hover:bg-indigo-100 border border-indigo-100"
            }`}>
            <Shield size={12} /> Admin
          </button>
        )}
      </nav>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <main className={`${isEditorialTheme ? "editorial-main" : "p-4 md:p-8"} flex-1 overflow-y-auto relative`}>
        {isEditorialTheme && activeNavItem && (
          <div className="editorial-page-heading">
            <div>
              <h2>{activeNavItem.l}</h2>
            </div>
            <span>1 USD = <strong>{usdThb.toFixed(2)} THB</strong></span>
          </div>
        )}
        {!dataReady ? (
          <TabSkeleton />
        ) : (
          <Suspense fallback={<TabSkeleton />}>
            {/*
              Keep-alive pattern: render once visited, hide with CSS instead of unmounting.
              This prevents re-fetch/re-render when switching tabs.
            */}
            <div style={{ display: activeTab === 'summary' ? undefined : 'none' }}>
              {visitedTabs.has('summary') && <SummaryTab transactions={transactions} dividends={dividends} capital={capital} marketDataStore={marketDataStore} usdThb={usdThb} lastSync={lastSync} user={user} onAddTrade={() => handleAddClick("trade")} onNavigate={handleTabChange} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'allocation' ? undefined : 'none' }}>
              {visitedTabs.has('allocation') && <InsightsTab transactions={transactions} marketDataStore={marketDataStore} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'trades' ? undefined : 'none' }}>
              {visitedTabs.has('trades') && <TransactionTab data={transactions} isReadOnly={isReadOnly} onAdd={() => handleAddClick("trade")} onEdit={(i) => handleEditClick("trade", i)} onDelete={(id) => handleDeleteClick("Transactions", id)} searchTerm={searchTerm} isPro={isPro} onExport={handleExportDataLocal} onImportClick={(e) => { if (!checkLimit()) e.preventDefault(); }} onImport={(e) => handleImportDataLocal(e, "transactions", supabase, user.uid, setNotification, () => fetchCycle(user.uid), checkLimit)} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'capital' ? undefined : 'none' }}>
              {visitedTabs.has('capital') && <CapitalTab data={capital} isReadOnly={isReadOnly} onAdd={() => handleAddClick("cap")} onEdit={(i) => handleEditClick("cap", i)} onDelete={(id) => handleDeleteClick("Capital", id)} usdThb={usdThb} isPro={isPro} onExport={handleExportDataLocal} onImportClick={(e) => { if (!checkLimit()) e.preventDefault(); }} onImport={(e) => handleImportDataLocal(e, "capital", supabase, user.uid, setNotification, () => fetchCycle(user.uid), checkLimit)} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'dividends' ? undefined : 'none' }}>
              {visitedTabs.has('dividends') && <DividendTab data={dividends} isReadOnly={isReadOnly} onAdd={() => handleAddClick("div")} onEdit={(i) => handleEditClick("div", i)} onDelete={(id) => handleDeleteClick("Dividend", id)} searchTerm={searchTerm} usdThb={usdThb} isPro={isPro} onExport={handleExportDataLocal} onImportClick={(e) => { if (!checkLimit()) e.preventDefault(); }} onImport={(e) => handleImportDataLocal(e, "dividends", supabase, user.uid, setNotification, () => fetchCycle(user.uid), checkLimit)} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'settled' ? undefined : 'none' }}>
              {visitedTabs.has('settled') && <RealizedPLTab transactions={transactions} isReadOnly={isReadOnly} usdThb={usdThb} onEdit={(item) => handleEditClick("trade", item)} onDelete={(id) => handleDeleteClick("Transactions", id)} isPro={isPro} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'holdings' ? undefined : 'none' }}>
              {visitedTabs.has('holdings') && <HoldingTab transactions={transactions} isReadOnly={isReadOnly} holdingsSummary={holdingsSummary} usdThb={usdThb} marketDataStore={marketDataStore} onExport={handleExportDataLocal} onRefresh={() => fetchCycle(user.uid)} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'xd' ? undefined : 'none' }}>
              {visitedTabs.has('xd') && <XDTab transactions={transactions} holdingsSummary={holdingsSummary} usdThb={usdThb} user={user} dividendPatterns={dividendPatterns} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'fundamental' ? undefined : 'none' }}>
              {visitedTabs.has('fundamental') && <FundamentalTab {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'pivot' ? undefined : 'none' }}>
              {visitedTabs.has('pivot') && <PivotTab {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'tax' ? undefined : 'none' }}>
              {visitedTabs.has('tax') && <TaxTab user={user} setNotification={setNotification} {...commonTabProps} />}
            </div>
            <div style={{ display: activeTab === 'privacy' ? undefined : 'none' }}>
              {visitedTabs.has('privacy') && <Privacy isDarkMode={isDarkMode} onBack={() => { handleTabChange("summary"); window.scrollTo(0, 0); }} />}
            </div>
            <div style={{ display: activeTab === 'terms' ? undefined : 'none' }}>
              {visitedTabs.has('terms') && <Terms isDarkMode={isDarkMode} onBack={() => { handleTabChange("summary"); window.scrollTo(0, 0); }} />}
            </div>
            <div style={{ display: activeTab === 'status' ? undefined : 'none' }}>
              {visitedTabs.has('status') && <SystemStatus isDarkMode={isDarkMode} onBack={() => { handleTabChange("summary"); window.scrollTo(0, 0); }} />}
            </div>
            {activeTab === 'admin' && isAdminRole && (
              <AdminTab isDarkMode={isDarkMode} setNotification={setNotification} isSystemAdmin={isSystemAdminOnly} marketDataStore={marketDataStore} user={user} ADMIN_EMAIL={process.env.REACT_APP_ADMIN_EMAIL} themeConfig={themeConfig} />
            )}
          </Suspense>
        )}

        <div className="editorial-risk-footer mt-10 mb-10 space-y-1 opacity-50">
          <p className="text-center text-[12px] font-bold tracking-wider uppercase">"การลงทุนมีความเสี่ยง" ผู้ลงทุนควรศึกษาข้อมูล ก่อนตัดสินใจลงทุน</p>
          <p className="text-center text-[11px] font-bold tracking-wider uppercase">Investment involves risk. Investors should study information before making decisions.</p>
        </div>

        <Suspense fallback={null}>
        </Suspense>
      </main>

      {!isReadOnly && (
        <div className="mobile-quick-add md:hidden">
          {isQuickAddOpen && (
            <div className="mobile-quick-add-menu" role="menu" aria-label="เพิ่มรายการด่วน">
              <button type="button" role="menuitem" onClick={() => { handleAddClick("trade"); setIsQuickAddOpen(false); }}>ซื้อหรือขายหุ้น</button>
              <button type="button" role="menuitem" onClick={() => { handleAddClick("div"); setIsQuickAddOpen(false); }}>เงินปันผล</button>
              <button type="button" role="menuitem" onClick={() => { handleAddClick("cap"); setIsQuickAddOpen(false); }}>เงินทุน</button>
            </div>
          )}
          <button type="button" className="mobile-quick-add-button" onClick={() => setIsQuickAddOpen((value) => !value)} aria-expanded={isQuickAddOpen} aria-label={isQuickAddOpen ? "ปิดเมนูเพิ่มรายการ" : "เพิ่มรายการด่วน"}>
            {isQuickAddOpen ? <X size={20} /> : <Plus size={20} />}<span>เพิ่มรายการ</span>
          </button>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showUpgradeModal && (
        <Suspense fallback={null}>
          <UpgradeModal onClose={() => setShowUpgradeModal(false)} isDarkMode={isDarkMode} user={user} />
        </Suspense>
      )}
      {modal.type === "trade" && (
        <AccessibleDialog onClose={() => setModal({ type: null, editing: null })} label="เพิ่มรายการซื้อขาย">
          <TransactionForm onClose={() => setModal({ type: null, editing: null })} userId={user.uid} isDarkMode={isDarkMode} callAI={(b64) => callGeminiVisionUltimate(b64)} editingItem={modal.editing} transactions={transactions} onSaveSuccess={(data) => updateLocalData('transactions', data)} onUpgradeClick={() => setShowUpgradeModal(true)} isPro={isPro} userRole={role} profile={profile} themeConfig={themeConfig} />
        </AccessibleDialog>
      )}
      {modal.type === "div" && (
        <AccessibleDialog onClose={() => setModal({ type: null, editing: null })} label="เพิ่มรายการเงินปันผล">
          <DividendForm onClose={() => setModal({ type: null, editing: null })} userId={user.uid} isDarkMode={isDarkMode} editingItem={modal.editing} onSaveSuccess={(data) => updateLocalData('dividends', data)} setNotification={setNotification} themeConfig={themeConfig} />
        </AccessibleDialog>
      )}
      {modal.type === "cap" && (
        <AccessibleDialog onClose={() => setModal({ type: null, editing: null })} label="เพิ่มรายการเงินทุน">
          <CapitalForm onClose={() => setModal({ type: null, editing: null })} userId={user.uid} isDarkMode={isDarkMode} editingItem={modal.editing} onSaveSuccess={(data) => updateLocalData('capitals', data)} setNotification={setNotification} themeConfig={themeConfig} />
        </AccessibleDialog>
      )}
      {notification && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-4 rounded-[1rem] font-black text-xs shadow-2xl z-[700] flex items-center gap-3 border transition-all animate-in slide-in-from-bottom-5 duration-300 ${isDarkMode ? "bg-emerald-600 border-emerald-500 text-white" : "bg-white border-emerald-200 text-emerald-600"}`}>
          <Check size={18} /> <span>{notification}</span>
        </div>
      )}
      <ConfirmActionDialog open={Boolean(deleteTarget)} onClose={() => !isDeleting && setDeleteTarget(null)} onConfirm={confirmDelete} title="ลบรายการนี้หรือไม่?" description="รายการนี้จะถูกลบออกจากพอร์ตอย่างถาวร กรุณาตรวจสอบก่อนยืนยัน" confirmLabel="ลบถาวร" tone="danger" isBusy={isDeleting} isDarkMode={isDarkMode} />
      <Suspense fallback={null}>
        <CookieBanner isDarkMode={isDarkMode} onGoToPrivacy={() => { handleTabChange("privacy"); window.scrollTo(0, 0); }} />
      </Suspense>
    </div>
  );
}
